<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import type { ContractTypeInDb, ContractTypeUsage } from '@/api';
import { ContractTypesService } from '@/services/contractManagement/contractTypesService';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaFormField from '../../base/GaiaFormField.vue';
import GaiaModal from '../../base/GaiaModal.vue';
import GaiaSpinner from '../../base/GaiaSpinner.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaSelect from '../../base/selects/GaiaSelect.vue';
import GaiaBanner from '../../composite/GaiaBanner.vue';

/**
 * Delete a contract type, after showing how much still uses it. A replacement
 * moves its contracts, playbooks, clauses and extraction prompts onto another
 * type; without one, contracts lose their type, playbooks and clauses that had
 * only this type become unrestricted, and such extraction prompts standalone.
 */
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  contractType: {
    type: Object as PropType<ContractTypeInDb | null>,
    default: null,
  },
});

const emit = defineEmits<{ 'update:show': [value: boolean] }>();

const contractTypeStore = useContractTypeStore();
const message = useGaiaToast();

const usage = ref<ContractTypeUsage | null>(null);
const usageLoading = ref(false);
const usageError = ref<string | null>(null);
const replacementUid = ref<string | null>(null);
const deleting = ref(false);
const deleteError = ref<string | null>(null);

const replacementOptions = computed(() =>
  contractTypeStore.uidOptions.filter(option => option.value !== props.contractType?.uid)
);

const inUse = computed(
  () =>
    usage.value !== null &&
    usage.value.contract_count +
      usage.value.playbook_count +
      usage.value.clause_count +
      usage.value.extraction_prompt_count >
      0
);

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** "a", "a and b", "a, b and c" — over the counts that aren't zero. */
function countList(counts: [number, string][]): string {
  const items = counts.filter(([count]) => count > 0).map(([count, noun]) => plural(count, noun));
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

const usageSummary = computed(() => {
  if (!usage.value) return '';
  const { contract_count, playbook_count, clause_count, extraction_prompt_count } = usage.value;
  return `Used by ${countList([
    [contract_count, 'contract'],
    [playbook_count, 'playbook'],
    [clause_count, 'clause'],
    [extraction_prompt_count, 'extraction prompt'],
  ])}.`;
});

const replacementName = computed(
  () => contractTypeStore.contractTypes.find(type => type.uid === replacementUid.value)?.name
);

/** What happens to everything that uses the type, one line per outcome. */
const consequences = computed<string[]>(() => {
  if (!usage.value) return [];
  if (replacementName.value) {
    return [`They will all be moved to ${replacementName.value}.`];
  }
  const {
    contract_count,
    playbook_count,
    clause_count,
    sole_type_playbook_count,
    sole_type_clause_count,
    extraction_prompt_count,
  } = usage.value;
  const lines: string[] = [];
  if (contract_count > 0) {
    lines.push(`${plural(contract_count, 'contract')} will have no contract type.`);
  }
  // A playbook or clause without any type left applies to every contract type.
  const unrestricted = countList([
    [sole_type_playbook_count, 'playbook'],
    [sole_type_clause_count, 'clause'],
  ]);
  if (unrestricted) {
    lines.push(`${unrestricted} will no longer be limited to a contract type.`);
  }
  const keepOthers = countList([
    [playbook_count - sole_type_playbook_count, 'playbook'],
    [clause_count - sole_type_clause_count, 'clause'],
  ]);
  if (keepOthers) {
    lines.push(`${keepOthers} will keep their other contract types.`);
  }
  // Usage has no sole-type count for prompts.
  if (extraction_prompt_count > 0) {
    lines.push(
      `${plural(extraction_prompt_count, 'extraction prompt')} will keep their other contract types, or become standalone.`
    );
  }
  return lines;
});

async function loadUsage(uid: string): Promise<void> {
  usageLoading.value = true;
  usageError.value = null;
  try {
    usage.value = await ContractTypesService.usage(uid);
  } catch (error) {
    logger.error('Failed to load contract type usage', error);
    usageError.value =
      error instanceof Error ? error.message : 'Could not check what uses this type.';
  } finally {
    usageLoading.value = false;
  }
}

watch(
  () => props.show,
  show => {
    if (!show || !props.contractType) return;
    usage.value = null;
    replacementUid.value = null;
    deleteError.value = null;
    void loadUsage(props.contractType.uid);
  },
  { immediate: true }
);

async function confirmDelete(): Promise<void> {
  const contractType = props.contractType;
  if (!contractType) return;
  deleting.value = true;
  deleteError.value = null;
  try {
    await contractTypeStore.deleteContractType(contractType.uid, replacementUid.value);
    message.success(`${contractType.name} deleted.`);
    emit('update:show', false);
  } catch (error) {
    logger.error('Failed to delete contract type', error);
    deleteError.value = extractErrorDetail(error, 'Failed to delete the contract type.');
  } finally {
    deleting.value = false;
  }
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="520px"
    :title="`Delete “${props.contractType?.name ?? ''}”?`"
    :mask-closable="!deleting"
    :close-on-esc="!deleting"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <n-flex vertical :size="14" :wrap="false">
      <gaia-banner v-if="deleteError" type="error">{{ deleteError }}</gaia-banner>
      <n-flex v-if="usageLoading" align="center" :size="8">
        <gaia-spinner size="small" />
        <gaia-text size="body" tone="subtle">Checking what uses this type…</gaia-text>
      </n-flex>
      <gaia-banner v-else-if="usageError" type="error">{{ usageError }}</gaia-banner>
      <template v-else-if="usage">
        <gaia-text v-if="!inUse" size="body" tone="subtle"> Nothing uses this type. </gaia-text>
        <template v-else>
          <gaia-banner type="warning">{{ usageSummary }}</gaia-banner>
          <gaia-form-field label="Move them to" hint="Optional">
            <gaia-select
              v-model:value="replacementUid"
              :options="replacementOptions"
              placeholder="No replacement"
              clearable
              filterable
            />
          </gaia-form-field>
          <n-flex vertical :size="4" :wrap="false">
            <gaia-text v-if="!replacementName" size="body" weight="semibold" tone="ink">
              Without a replacement:
            </gaia-text>
            <gaia-text v-for="line in consequences" :key="line" size="body" tone="subtle">
              {{ line }}
            </gaia-text>
          </n-flex>
        </template>
      </template>
    </n-flex>
    <template #action>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button :disabled="deleting" @click="emit('update:show', false)">Cancel</gaia-button>
        <gaia-button
          type="error"
          :loading="deleting"
          :disabled="usageLoading || !usage"
          @click="confirmDelete"
        >
          Delete
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
